import { useEffect, useState } from 'react'

import { api } from '../../api/client'
import type { EagleSonda } from '../../api/types'
import { ErrorBanner } from '../common'
import { t } from '../../i18n/lingua'

/**
 * The option of the «Latenza verso un indirizzo» widget: an IP address, a
 * name for it and the probe that pings it. «Collega» asks the Control
 * Plane for the target (a CI for that address, created once, with ping and
 * the fast ping on and the probe as its observation point) and stores its
 * id in the widget options; the widget then draws the latency series like
 * any other parameter.
 */
export function SceltaBersaglio({ address, ciId, ciName, probeName, onCambia }: {
  address: string
  ciId: string
  ciName: string
  probeName: string
  onCambia: (scelta: { ci_id: string; ci_name: string; address: string; probe_id: string; probe_name: string }) => void
}) {
  const [indirizzo, setIndirizzo] = useState(address)
  const [nome, setNome] = useState('')
  const [sonda, setSonda] = useState('')
  const [sonde, setSonde] = useState<EagleSonda[]>([])
  const [busy, setBusy] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)
  useEffect(() => {
    let vivo = true
    void api.eagleSonde().then((r) => { if (vivo) setSonde((r.probes ?? []).filter((p) => p.alive !== null && p.role !== 'off')) }).catch(() => undefined)
    return () => { vivo = false }
  }, [])

  async function collega() {
    setBusy(true)
    setErrore(null)
    try {
      const esito = await api.eagleBersaglio({ address: indirizzo.trim(), name: nome.trim() || null, probe_id: sonda || null })
      const nomeSonda = sonde.find((p) => p.id === (esito.probe_id ?? sonda))?.name ?? ''
      onCambia({ ci_id: esito.ci_id, ci_name: esito.name, address: esito.address, probe_id: esito.probe_id ?? sonda, probe_name: nomeSonda })
    } catch (e) {
      setErrore(e)
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <label className="field span-2"><span className="field__label">{t('Indirizzo IP da raggiungere')}</span>
        <input type="text" value={indirizzo} onChange={(e) => setIndirizzo(e.target.value)} placeholder="8.8.8.8" spellCheck={false} /></label>
      <label className="field span-2"><span className="field__label">{t('Nome (facoltativo)')}</span>
        <input type="text" value={nome} onChange={(e) => setNome(e.target.value)} placeholder={t('es. Internet da Sesto')} /></label>
      <label className="field span-2"><span className="field__label">{t('Sonda che misura')}</span>
        <select value={sonda} onChange={(e) => setSonda(e.target.value)}>
          <option value="">{t('scelta automatica')}</option>
          {sonde.map((p) => <option key={p.id} value={p.id}>{p.name}{p.alive === false ? ` · ${t('ferma')}` : ''}</option>)}
        </select></label>
      <div className="field span-6">
        <button type="button" className="secondary" onClick={() => void collega()} disabled={busy || !indirizzo.trim()}>
          {busy ? t('Collego…') : ciId ? t('Cambia bersaglio') : t('Collega')}
        </button>
        {ciId && (
          <span className="field__hint">
            {t('Collegato: {name} ({address}){probe}', { name: ciName || address, address, probe: probeName ? ` · ${t('misura {probe}', { probe: probeName })}` : '' })}
          </span>
        )}
        {!ciId && <span className="field__hint">{t('«Collega» crea (una volta) il CI per l’indirizzo con ping e ping rapido accesi dalla sonda scelta; la latenza arriva entro un minuto.')}</span>}
        <ErrorBanner error={errore} />
      </div>
    </>
  )
}
